<template>
    <div class="music-note-container">
        <div ref="vexContainer" class="vex-container"></div>
        <div v-if="error" class="error-message">{{ error }}</div>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, nextTick } from "vue";
import {
    Renderer,
    Stave,
    StaveNote,
    Voice,
    Formatter,
    Accidental,
    Stem,
} from "vexflow";

interface Props {
    note?: string;
    clef?: string;
    duration?: string;
    width?: number;
    height?: number;
    overlayNote?: string;
    overlayColor?: string;
}

const props = withDefaults(defineProps<Props>(), {
    note: "C4",
    clef: "treble",
    duration: "quarter",
    width: 200,
    height: 120,
    overlayColor: "#dc3545",
});

const vexContainer = ref<HTMLDivElement>();
const error = ref<string>("");

// Map duration names to VexFlow duration values
const durationMap: Record<string, string> = {
    whole: "w",
    half: "h",
    quarter: "q",
    eighth: "8",
    sixteenth: "16",
    "thirty-second": "32",
    "sixty-fourth": "64",
};

// Parse note string (e.g., "C4", "F#5", "Bb3") into VexFlow format
function parseNote(noteStr: string): { keys: string[]; accidentals: string[] } {
    if (!noteStr) {
        return { keys: ["C/4"], accidentals: [] };
    }

    // Remove spaces and convert to uppercase
    const cleanNote = noteStr.replace(/\s/g, "").toUpperCase();

    // Match pattern like C4, F#5, Bb3, etc.
    const match = cleanNote.match(/^([A-G])([#Bb]?)(\d+)$/);

    if (!match) {
        throw new Error(`Invalid note format: ${noteStr}`);
    }

    const [, letter, accidental, octave] = match;
    const key = `${letter}/${octave}`;
    const accidentals: string[] = [];

    if (accidental) {
        if (accidental === "#") {
            accidentals.push("#");
        } else if (accidental === "B" || accidental === "b") {
            accidentals.push("b");
        }
    }

    return { keys: [key], accidentals };
}

// Determine if note should have stem down (C5 and above)
function shouldStemDown(noteStr: string): boolean {
    if (!noteStr) return false;

    const cleanNote = noteStr.replace(/\s/g, "").toUpperCase();
    const match = cleanNote.match(/^([A-G])([#Bb]?)(\d+)$/);

    if (!match) return false;

    const [, letter, , octave] = match;
    const octaveNum = parseInt(octave);

    // C5 and above should have stems down
    if (octaveNum > 5) return true;
    if (octaveNum === 5 && letter >= "C") return true;

    return false;
}

function getDuration(durationStr: string): string {
    if (!durationStr) return "q"; // default to quarter note

    const mapped = durationMap[durationStr.toLowerCase()];
    if (mapped) return mapped;

    // If it's already in VexFlow format (q, h, w, etc.), use it directly
    if (/^[whq]$|^[0-9]+$/.test(durationStr)) {
        return durationStr;
    }

    return "q"; // fallback to quarter note
}

function renderNotation() {
    if (!vexContainer.value) return;

    try {
        error.value = "";

        // Clear previous content
        vexContainer.value.innerHTML = "";

        // Create VexFlow renderer
        const renderer = new Renderer(
            vexContainer.value,
            Renderer.Backends.SVG,
        );
        renderer.resize(props.width, props.height);
        const context = renderer.getContext();

        // Create stave
        const stave = new Stave(10, 10, props.width - 20);

        // Add clef
        const clefType = props.clef || "treble";
        if (["treble", "bass", "alto", "tenor"].includes(clefType)) {
            stave.addClef(clefType);
        } else {
            stave.addClef("treble"); // fallback
        }

        // Draw stave
        stave.setContext(context).draw();

        // Parse note and create StaveNote
        const { keys, accidentals } = parseNote(props.note || "C4");
        const duration = getDuration(props.duration || "quarter");

        const staveNote = new StaveNote({
            keys,
            duration,
            clef: props.clef || "treble",
            stemDirection: shouldStemDown(props.note || "C4")
                ? Stem.DOWN
                : Stem.UP,
        });

        // Add accidentals if present
        accidentals.forEach((acc, index) => {
            if (acc === "#") {
                staveNote.addModifier(new Accidental("#"), index);
            } else if (acc === "b") {
                staveNote.addModifier(new Accidental("b"), index);
            }
        });

        // Create overlay note if provided
        let overlayStaveNote: StaveNote | null = null;
        if (props.overlayNote) {
            const overlay = parseNote(props.overlayNote);
            overlayStaveNote = new StaveNote({
                keys: overlay.keys,
                duration,
                clef: props.clef || "treble",
                stemDirection: shouldStemDown(props.overlayNote)
                    ? Stem.DOWN
                    : Stem.UP,
            });

            // Add accidentals for overlay note
            overlay.accidentals.forEach((acc, index) => {
                if (acc === "#") {
                    overlayStaveNote!.addModifier(new Accidental("#"), index);
                } else if (acc === "b") {
                    overlayStaveNote!.addModifier(new Accidental("b"), index);
                }
            });

            // Set color for overlay note
            overlayStaveNote.setStyle({
                fillStyle: props.overlayColor,
                strokeStyle: props.overlayColor,
            });
        }

        // Calculate beat value based on note duration
        let beatValue: number;
        switch (duration) {
            case "w":
                beatValue = 1; // whole note gets 1 beat
                break;
            case "h":
                beatValue = 2; // half note gets 1 beat
                break;
            case "q":
                beatValue = 4; // quarter note gets 1 beat
                break;
            case "8":
                beatValue = 8; // eighth note gets 1 beat
                break;
            case "16":
                beatValue = 16; // sixteenth note gets 1 beat
                break;
            case "32":
                beatValue = 32;
                break;
            case "64":
                beatValue = 64;
                break;
            default:
                beatValue = 4;
                break;
        }

        // Main note voice
        const voice = new Voice({
            numBeats: 1,
            beatValue: beatValue,
        });
        voice.addTickable(staveNote);

        // Format and draw main note with centering
        const formatter = new Formatter();
        const staveWidth = props.width - 60;
        formatter.joinVoices([voice]).format([voice], staveWidth);

        // Center the note by adjusting the voice position
        const noteWidth = voice.getTickables()[0].getWidth();
        const centerOffset = (staveWidth - noteWidth) / 2;

        // Draw main voice
        voice.draw(context, stave);

        // Apply centering transform to main note
        const svgElement = context.svg;
        let mainNoteGroup = svgElement.querySelector(
            "g.vf-stavenote:last-child",
        );
        if (mainNoteGroup) {
            const currentTransform =
                mainNoteGroup.getAttribute("transform") || "";
            mainNoteGroup.setAttribute(
                "transform",
                `translate(${centerOffset}, 0) ${currentTransform}`,
            );
        }

        // Draw overlay note at the SAME position if provided
        if (overlayStaveNote) {
            const overlayVoice = new Voice({
                numBeats: 1,
                beatValue: beatValue,
            });
            overlayVoice.addTickable(overlayStaveNote);

            // Format overlay independently
            const overlayFormatter = new Formatter();
            overlayFormatter
                .joinVoices([overlayVoice])
                .format([overlayVoice], staveWidth);

            // Draw overlay voice
            overlayVoice.draw(context, stave);

            // Apply the SAME centering transform to overlay note
            const overlayNoteGroup = svgElement.querySelector(
                "g.vf-stavenote:last-child",
            );
            if (overlayNoteGroup) {
                const currentTransform =
                    overlayNoteGroup.getAttribute("transform") || "";
                overlayNoteGroup.setAttribute(
                    "transform",
                    `translate(${centerOffset}, 0) ${currentTransform}`,
                );
            }
        }
    } catch (err) {
        console.error("VexFlow rendering error:", err);
        error.value = `Error rendering note: ${err instanceof Error ? err.message : "Unknown error"}`;
    }
}

// Watch for prop changes and re-render
watch(
    [
        () => props.note,
        () => props.clef,
        () => props.duration,
        () => props.width,
        () => props.height,
        () => props.overlayNote,
        () => props.overlayColor,
    ],
    () => {
        nextTick(() => {
            renderNotation();
        });
    },
);

onMounted(() => {
    nextTick(() => {
        renderNotation();
    });
});
</script>

<style scoped>
.music-note-container {
    display: inline-block;
    border: 1px solid #ccc;
    border-radius: 4px;
    background: white;
    padding: 5px;
}

.vex-container {
    display: flex;
    justify-content: center;
    align-items: center;
}

.vex-container svg {
    border-radius: 3px;
}

.error-message {
    color: #dc3545;
    font-size: 12px;
    text-align: center;
    padding: 5px;
    background: #f8d7da;
    border: 1px solid #f5c6cb;
    border-radius: 3px;
    margin-top: 5px;
}
</style>
